import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemBanco } from '@/pages/gerenciar/direcao/mensagemBanco';
import Modal from '@/components/ui/Modal';
import { rotuloCaixaOpcao, type CaixaEquipe, type Notificar } from './utils';

interface Props {
  conviteId: number;
  nome: string;
  caixas: CaixaEquipe[];
  atual: number | null;
  aoFechar: () => void;
  aoSalvo: () => void;
  notificar: Notificar;
}

// Troca o caixa de um convite já existente (mesma lista do "Qual caixa?" do FolhaChamar).
export default function FolhaTrocarCaixa({
  conviteId,
  nome,
  caixas,
  atual,
  aoFechar,
  aoSalvo,
  notificar,
}: Props) {
  const [escolhido, setEscolhido] = useState<number | null>(atual);
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    try {
      const { data, error } = await supabase.rpc('equipe_definir_caixa', {
        p_convite: conviteId,
        p_caixa: escolhido,
      });
      if (error) throw error;
      const resp = data as { ok?: boolean; mensagem?: string; erro?: string } | null;
      if (resp && resp.ok === false) {
        notificar(resp.erro || 'Não foi possível trocar o caixa.', 'erro');
        return;
      }
      notificar(resp?.mensagem || 'Caixa atualizado.');
      aoSalvo();
    } catch (e) {
      notificar(mensagemBanco(e), 'erro');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal aberto titulo="Trocar caixa" aoFechar={aoFechar}>
      <p className="text-[14px] leading-relaxed text-foreground-600">
        Em qual caixa {nome ? <strong className="font-semibold text-foreground-950">{nome}</strong> : 'esta pessoa'} vai operar?
      </p>

      <div className="mt-3 flex flex-col gap-2">
        {caixas.map((c) => (
          <OpcaoCaixa
            key={c.caixa}
            ativo={escolhido === c.caixa}
            rotulo={rotuloCaixaOpcao(c)}
            onClick={() => setEscolhido(c.caixa)}
          />
        ))}
        <OpcaoCaixa ativo={escolhido == null} rotulo="Escolher na hora" onClick={() => setEscolhido(null)} />
      </div>

      {caixas.length === 0 && (
        <p className="mt-3 text-[13px] leading-relaxed text-foreground-500">
          Nenhum caixa criado neste evento. A pessoa vai escolher na hora.
        </p>
      )}

      <div className="mt-5 flex gap-2">
        <button
          type="button"
          onClick={aoFechar}
          disabled={salvando}
          className="flex h-12 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-foreground-100 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-foreground-200 disabled:opacity-60"
        >
          Voltar
        </button>
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="flex h-12 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 text-[14px] font-bold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </Modal>
  );
}

function OpcaoCaixa({
  ativo,
  rotulo,
  onClick,
}: {
  ativo: boolean;
  rotulo: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-[48px] cursor-pointer items-center justify-between gap-3 rounded-2xl border px-4 text-left text-[14px] font-semibold transition-colors ${
        ativo
          ? 'border-primary-500 bg-primary-50 text-primary-900'
          : 'border-secondary-300 bg-white text-foreground-800 hover:bg-background-100'
      }`}
    >
      <span className="truncate">{rotulo}</span>
      {ativo && (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
          <i className="ri-check-line" />
        </span>
      )}
    </button>
  );
}